import Phaser from "phaser";
import { GAME } from "@/config";

/**
 * In-scene camera pan (v0.9 RFC §3): the world is taller than the canvas and
 * the player drags to slide the camera through it. DOM chrome stays pinned.
 *
 * The gesture math is PURE (exported, unit-tested); wirePan() binds it to a
 * scene's input + main camera and removes every listener on SHUTDOWN (the
 * global-listener discipline that has bitten before).
 */

import { clampScroll, flingStep, isTap, PAN } from "./panMath";

export { clampScroll, flingStep, isTap, PAN } from "./panMath";

export interface PanHandle {
  /** Current camera scrollY (for tests/debug). */
  readonly scrollY: () => number;
}

/**
 * Wire drag-to-pan on a scene whose world is `worldH` px tall. Taps (per
 * isTap) are re-emitted as a "stagetap" scene event with WORLD coordinates —
 * the hook the curated tap-a-prop layer listens on. Returns a handle.
 */
export function wirePan(scene: Phaser.Scene, worldH: number): PanHandle {
  if (!Number.isFinite(worldH) || worldH < GAME.height) {
    // Fail loud + field-named: a pannable stage must be at least one screen.
    throw new Error(`wirePan: worldH must be >= GAME.height (${GAME.height}), got ${worldH}`);
  }
  const cam = scene.cameras.main;
  cam.setBounds(0, 0, GAME.width, worldH);

  let dragging = false;
  let startY = 0;        // pointer y at gesture start (screen px)
  let startScroll = 0;   // camera scrollY at gesture start
  let startT = 0;
  let lastY = 0;
  let lastT = 0;
  let vel = 0;           // px per ~16ms step, world-space
  let travelAcc = 0;     // accumulated path travel (world px) — a drag out
                         // and back must NOT read as a tap
  let flinging = false;

  // Screen px → world px (Scale.FIT keeps a uniform factor). Guard the
  // pre-layout frame where displaySize can still be 0 (review finding).
  const toWorld = (screenPx: number): number => {
    const h = scene.scale.displaySize.height;
    return h > 0 ? screenPx * (GAME.height / h) : screenPx;
  };

  const onDown = (p: Phaser.Input.Pointer): void => {
    dragging = true;
    flinging = false;
    startY = lastY = p.y;
    startScroll = cam.scrollY;
    // one clock for the whole gesture (downTime has a different origin)
    startT = lastT = scene.time.now;
    travelAcc = 0;
    vel = 0;
  };
  const onMove = (p: Phaser.Input.Pointer): void => {
    if (!dragging) return;
    const now = scene.time.now;
    const dy = toWorld(p.y - lastY);
    travelAcc += Math.abs(dy);
    if (now > lastT) vel = (dy / (now - lastT)) * 16;
    lastY = p.y;
    lastT = now;
    cam.scrollY = clampScroll(startScroll - toWorld(p.y - startY), worldH, GAME.height);
  };
  const onUp = (p: Phaser.Input.Pointer): void => {
    if (!dragging) return;
    dragging = false;
    const travel = Math.max(toWorld(p.getDistance()), travelAcc);
    const elapsed = scene.time.now - startT;
    if (isTap(travel, elapsed)) {
      const world = cam.getWorldPoint(p.x, p.y);
      scene.events.emit("stagetap", world.x, world.y);
      return;
    }
    // A held-still release must not fling on a stale velocity sample.
    if (scene.time.now - lastT > 120) vel = 0;
    flinging = Math.abs(vel) >= PAN.flingMinSpeed;
  };
  const onUpdate = (): void => {
    if (!flinging) return;
    vel = flingStep(vel);
    if (vel === 0) { flinging = false; return; }
    const next = clampScroll(cam.scrollY - vel, worldH, GAME.height);
    if (next === cam.scrollY) { flinging = false; vel = 0; return; } // hit a bound
    cam.scrollY = next;
  };

  scene.input.on(Phaser.Input.Events.POINTER_DOWN, onDown);
  scene.input.on(Phaser.Input.Events.POINTER_MOVE, onMove);
  scene.input.on(Phaser.Input.Events.POINTER_UP, onUp);
  // A release outside the canvas must also end the gesture, or the next
  // move keeps dragging a dead pointer (review finding, v0.9).
  scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, onUp);
  scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.input.off(Phaser.Input.Events.POINTER_DOWN, onDown);
    scene.input.off(Phaser.Input.Events.POINTER_MOVE, onMove);
    scene.input.off(Phaser.Input.Events.POINTER_UP, onUp);
    scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, onUp);
    scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate);
  });

  return { scrollY: () => cam.scrollY };
}
